// Search Integration for Real-World Data
// Provides restaurant, hotel, weather, and activity suggestions

export interface SearchResult {
  type: 'restaurant' | 'hotel' | 'activity' | 'weather' | 'transportation';
  name: string;
  description: string;
  location: string;
  rating?: number;
  priceRange?: '$' | '$$' | '$$$' | '$$$$';
  url?: string;
  imageUrl?: string;
  metadata?: any;
}

export interface WeatherData {
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  recommendation: string;
}

export interface DatePlanSuggestion {
  title: string;
  activities: SearchResult[];
  restaurants: SearchResult[];
  weather: WeatherData;
  timeline: string[];
  budget: number;
  tips: string[];
}

class SearchIntegrationService {
  private mockMode = true; // Set to false when real APIs are available
  
  // Mock data for development - replace with real API calls later
  private mockRestaurants = [
    {
      type: 'restaurant' as const,
      name: 'Corner House Ice Cream',
      description: 'Famous for Death by Chocolate, perfect for conversations',
      location: 'Brigade Road, Bangalore',
      rating: 4.5,
      priceRange: '$$' as const,
      metadata: {
        cuisine: 'Desserts & Ice Cream',
        ambiance: 'Casual, Date-friendly',
        specialty: 'Death by Chocolate'
      }
    },
    {
      type: 'restaurant' as const,
      name: 'The Hole in the Wall Cafe',
      description: 'Cozy breakfast spot with amazing coffee',
      location: 'Koramangala, Bangalore',
      rating: 4.3,
      priceRange: '$$' as const,
      metadata: {
        cuisine: 'Continental, Coffee',
        ambiance: 'Intimate, Artistic',
        specialty: 'Single origin coffee'
      }
    },
    {
      type: 'restaurant' as const,
      name: 'MTR 1924',
      description: 'Heritage South Indian restaurant with authentic flavors',
      location: 'Lalbagh Road, Bangalore',
      rating: 4.6,
      priceRange: '$' as const,
      metadata: {
        cuisine: 'South Indian',
        ambiance: 'Traditional, Family-friendly',
        specialty: 'Rava Idli, Filter Coffee'
      }
    }
  ];
  
  private mockActivities = [
    {
      type: 'activity' as const,
      name: 'Lalbagh Botanical Gardens',
      description: 'Beautiful gardens perfect for morning walks and photography',
      location: 'Mavalli, Bangalore',
      rating: 4.4,
      metadata: {
        duration: '2-3 hours',
        bestTime: 'Early morning or evening',
        photoOpportunity: 'High',
        activityType: 'Nature, Photography'
      }
    },
    {
      type: 'activity' as const,
      name: 'National Gallery of Modern Art',
      description: 'Contemporary art exhibitions and cultural events',
      location: 'Manikyavelu Mansion, Bangalore',
      rating: 4.2,
      metadata: {
        duration: '1-2 hours',
        bestTime: 'Afternoon',
        intellectualStimulation: 'High',
        activityType: 'Art, Culture'
      }
    },
    {
      type: 'activity' as const,
      name: 'Church Street Social',
      description: 'Vibrant area with cafes, bookstores, and street art',
      location: 'Church Street, Bangalore',
      rating: 4.3,
      metadata: {
        duration: '3-4 hours',
        bestTime: 'Evening',
        socialVibes: 'High',
        activityType: 'Urban exploration, Shopping'
      }
    }
  ];
  
  async searchRestaurants(location: string, cuisine?: string, priceRange?: string): Promise<SearchResult[]> {
    if (this.mockMode) {
      // Filter mock data based on criteria
      return this.mockRestaurants.filter(restaurant => {
        if (cuisine && !restaurant.metadata.cuisine.toLowerCase().includes(cuisine.toLowerCase())) {
          return false;
        }
        if (priceRange && restaurant.priceRange !== priceRange) {
          return false;
        }
        return true;
      });
    }
    
    // Real API implementation would go here
    // For now, return mock data
    return this.mockRestaurants;
  }
  
  async searchActivities(location: string, type?: string): Promise<SearchResult[]> {
    if (this.mockMode) {
      return this.mockActivities.filter(activity => {
        if (type && !activity.metadata.activityType.toLowerCase().includes(type.toLowerCase())) {
          return false;
        }
        return true;
      });
    }
    
    // Real API implementation would go here
    return this.mockActivities;
  }
  
  async getWeather(location: string): Promise<WeatherData> {
    if (this.mockMode) {
      // Mock weather data for Bangalore
      return {
        temperature: 24,
        condition: 'Partly Cloudy',
        humidity: 65,
        windSpeed: 12,
        recommendation: 'Perfect weather for outdoor activities! Light jacket recommended for evening.'
      };
    }
    
    // Real weather API call would go here
    // Example: OpenWeatherMap, WeatherAPI, etc.
    return {
      temperature: 24,
      condition: 'Partly Cloudy',
      humidity: 65,
      windSpeed: 12,
      recommendation: 'Perfect weather for outdoor activities!'
    };
  }
  
  async generateDatePlan(preferences: {
    location: string;
    timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night';
    budget: 'budget-friendly' | 'moderate' | 'special-occasion';
    vibe: 'casual' | 'romantic' | 'adventurous' | 'cultural';
    interests: string[];
  }): Promise<DatePlanSuggestion> {
    
    const { location, timeOfDay, budget, vibe } = preferences;
    
    // Get weather for planning
    const weather = await this.getWeather(location);
    
    // Search for appropriate activities
    const activities = await this.searchActivities(location, vibe);
    
    // Search for restaurants based on budget
    const priceMap = {
      'budget-friendly': '$',
      'moderate': '$$',
      'special-occasion': '$$$'
    };
    const restaurants = await this.searchRestaurants(location, undefined, priceMap[budget]);
    
    // Generate timeline based on time of day
    const timeline = this.generateTimeline(timeOfDay, activities, restaurants);
    
    // Calculate estimated budget
    const estimatedBudget = this.calculateBudget(budget, activities, restaurants);
    
    // Generate contextual tips
    const tips = this.generateDateTips(vibe, weather, timeOfDay);
    
    return {
      title: `${vibe.charAt(0).toUpperCase() + vibe.slice(1)} ${timeOfDay} in ${location}`,
      activities: activities.slice(0, 2), // Top 2 activities
      restaurants: restaurants.slice(0, 2), // Top 2 restaurant options
      weather,
      timeline,
      budget: estimatedBudget,
      tips
    };
  }
  
  private generateTimeline(timeOfDay: string, activities: SearchResult[], restaurants: SearchResult[]): string[] {
    const timelines = {
      morning: [
        '9:00 AM - Meet for coffee and light breakfast',
        '10:30 AM - Activity: ' + (activities[0]?.name || 'Explore local area'),
        '12:30 PM - Lunch at ' + (restaurants[0]?.name || 'a nice local spot'),
        '2:00 PM - Wrap up with dessert or coffee'
      ],
      afternoon: [
        '2:00 PM - Meet for late lunch',
        '3:30 PM - Activity: ' + (activities[0]?.name || 'Explore together'),
        '5:30 PM - Coffee break and conversation',
        '7:00 PM - Evening walk or continue exploring'
      ],
      evening: [
        '6:00 PM - Meet for early dinner',
        '7:30 PM - Activity: ' + (activities[0]?.name || 'Evening exploration'),
        '9:00 PM - Dessert at ' + (restaurants[1]?.name || 'a cozy cafe'),
        '10:30 PM - Gentle end to perfect evening'
      ],
      night: [
        '8:00 PM - Meet for dinner',
        '9:30 PM - Activity: ' + (activities[0]?.name || 'Night exploration'),
        '11:00 PM - Late night coffee or dessert',
        '12:00 AM - End with promise of next adventure'
      ]
    };
    
    return timelines[timeOfDay as keyof typeof timelines] || timelines.evening;
  }
  
  private calculateBudget(level: string, activities: SearchResult[], restaurants: SearchResult[]): number {
    const budgetMap = {
      'budget-friendly': 800,  // ₹800 per person
      'moderate': 1500,        // ₹1500 per person
      'special-occasion': 3000 // ₹3000 per person
    };
    
    return budgetMap[level as keyof typeof budgetMap] || 1500;
  }
  
  private generateDateTips(vibe: string, weather: WeatherData, timeOfDay: string): string[] {
    const baseTips = [
      `Weather looks ${weather.condition.toLowerCase()} with ${weather.temperature}°C - ${weather.recommendation}`,
      'Remember to keep conversation flowing - ask about their interests!',
      'Be present and put phones away during activities'
    ];
    
    const vibeTips = {
      casual: ['Keep it relaxed and fun', 'Comfortable clothes recommended'],
      romantic: ['Focus on creating intimate moments', 'Small thoughtful gestures matter'],
      adventurous: ['Be open to trying new things', 'Energy and enthusiasm are key'],
      cultural: ['Ask thoughtful questions about exhibits/experiences', 'Share your perspectives']
    };
    
    const timeTips = {
      morning: ['Great choice for fresh conversations', 'Coffee always helps break ice'],
      afternoon: ['Perfect for extended exploring', 'Good natural lighting for photos'],
      evening: ['Most romantic time', 'Consider how to end thoughtfully'],
      night: ['Intimate and special', 'Plan safe transportation home']
    };
    
    return [
      ...baseTips,
      ...vibeTips[vibe as keyof typeof vibeTips] || [],
      ...timeTips[timeOfDay as keyof typeof timeTips] || []
    ];
  }
  
  // Helper method for Universe to get quick suggestions
  async getQuickSuggestions(context: string): Promise<string[]> {
    if (context.toLowerCase().includes('food') || context.toLowerCase().includes('restaurant')) {
      const restaurants = await this.searchRestaurants('Bangalore');
      return restaurants.map(r => `${r.name} - ${r.description}`);
    }
    
    if (context.toLowerCase().includes('activity') || context.toLowerCase().includes('fun')) {
      const activities = await this.searchActivities('Bangalore');
      return activities.map(a => `${a.name} - ${a.description}`);
    }
    
    return [
      'Corner House for classic date vibes',
      'Lalbagh Gardens for peaceful walks',
      'Church Street for urban exploration',
      'Art galleries for cultural discussions'
    ];
  }
}

// Export singleton instance
export const searchService = new SearchIntegrationService();

// Helper functions for AI prompts
export const getRestaurantSuggestion = async (criteria: string): Promise<string> => {
  const restaurants = await searchService.searchRestaurants('Bangalore');
  const suggestion = restaurants[Math.floor(Math.random() * restaurants.length)];
  return `${suggestion.name} - ${suggestion.description}. ${suggestion.metadata.specialty ? 'Famous for: ' + suggestion.metadata.specialty : ''}`;
};

export const getActivitySuggestion = async (vibe: string): Promise<string> => {
  const activities = await searchService.searchActivities('Bangalore', vibe);
  const suggestion = activities[Math.floor(Math.random() * activities.length)];
  return `${suggestion.name} - ${suggestion.description}. Best time: ${suggestion.metadata.bestTime}`;
};

export const getWeatherAdvice = async (): Promise<string> => {
  const weather = await searchService.getWeather('Bangalore');
  return `Currently ${weather.temperature}°C and ${weather.condition}. ${weather.recommendation}`;
};

export default searchService;